import { db } from '@/models/db';
import type { DeltaPacketRecord } from '@/models/hydrate.ts';
import { InstantOfferV2 } from '@/models/registry.ts';
import type { BaseStore } from '@/models/stores/base.ts';
import { type InstantOfferV2 as InstantOfferV2Type } from '@/models/types.ts';
import { makeAutoObservable, observable, type ObservableMap } from 'mobx';

class InstantOfferStoreV2
  implements BaseStore<InstantOfferV2, InstantOfferV2Type>
{
  store: ObservableMap<string, InstantOfferV2> = observable.map();

  // appraisal tracking_id -> array of instant offers
  storeIndexedByForeignKey: ObservableMap<string, InstantOfferV2[]> =
    observable.map();

  constructor() {
    makeAutoObservable(
      this,
      {
        store: observable.shallow,
        storeIndexedByForeignKey: observable.shallow,
      },
      { autoBind: true },
    );
  }

  private setInstantOfferField = <K extends keyof InstantOfferV2Type>(
    entry: InstantOfferV2,
    key: K,
    value: InstantOfferV2Type[K],
  ) => {
    (entry as Pick<InstantOfferV2, K>)[key] = value as InstantOfferV2[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<InstantOfferV2Type>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.instant_offers_v2.delete(delta.record_id);
      }
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.instant_offers_v2.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.instant_offers_v2.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.instant_offers_v2.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<InstantOfferV2Type>;
  }) {
    const existingInstantOffer = this.getById(delta.record_id);
    if (!existingInstantOffer) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof InstantOfferV2Type,
      InstantOfferV2Type[keyof InstantOfferV2Type],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingInstantOffer[key] === newValue) {
        continue;
      }

      this.setInstantOfferField(existingInstantOffer, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingInstantOffer);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<InstantOfferV2Type>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<InstantOfferV2Type>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete') {
      this.remove(delta.record_id);
      return;
    }

    if (hydrateIndexedDB) {
      db.instant_offers_v2.put(delta.data, delta.record_id);
    }

    this.upsert(delta.data);
  }

  private getOrCreateCollection(
    map: Map<string, InstantOfferV2[]>,
    key: string,
  ): InstantOfferV2[] {
    let arr = map.get(key);
    if (!arr) {
      arr = observable.array<InstantOfferV2>([], { deep: false });
      map.set(key, arr);
    }
    return arr;
  }

  // channel_id = appraisal tracking_id
  addToIndexes(offer: InstantOfferV2) {
    this.getOrCreateCollection(
      this.storeIndexedByForeignKey,
      offer.channel_id,
    ).push(offer);
  }

  removeFromIndexes(offer: InstantOfferV2) {
    const removeFrom = (arr: InstantOfferV2[]) => {
      const i = arr.findIndex((o) => o.id === offer.id);
      if (i >= 0) arr.splice(i, 1);
    };

    removeFrom(
      this.getOrCreateCollection(
        this.storeIndexedByForeignKey,
        offer.channel_id,
      ),
    );
  }

  get all() {
    return Array.from(this.store.values());
  }

  getById(id: string): InstantOfferV2 | undefined {
    return this.store.get(id);
  }

  add(input: InstantOfferV2Type): void {
    const instantOffer = new InstantOfferV2(input);

    this.store.set(instantOffer.id, instantOffer);
    this.addToIndexes(instantOffer);
  }

  upsert(instantOffer: InstantOfferV2Type) {
    const idToUse = instantOffer.id; // No String() casting needed for UUID
    const existingOffer = this.getById(idToUse);
    if (existingOffer) {
      this.replace({ id: idToUse, instantOffer });
    } else {
      this.add(instantOffer);
    }
  }

  save(offer: InstantOfferV2) {
    this.store.set(offer.id, offer);

    const arr = this.getOrCreateCollection(
      this.storeIndexedByForeignKey,
      offer.channel_id,
    );
    const idx = arr.findIndex((o) => o.id === offer.id);
    if (idx >= 0) arr.splice(idx, 1, offer);
  }

  replace({
    id,
    instantOffer,
  }: {
    id: string;
    instantOffer: InstantOfferV2Type;
  }) {
    const existingOffer = this.getById(id);
    if (!existingOffer) return;

    existingOffer.tracking_id = instantOffer.tracking_id;
    existingOffer.channel = instantOffer.channel;
    existingOffer.channel_id = instantOffer.channel_id;
    existingOffer.state = instantOffer.state;
    existingOffer.status = instantOffer.status;
    existingOffer.offer_type = instantOffer.offer_type;
    existingOffer.powertrain_type = instantOffer.powertrain_type;
    existingOffer.margin = instantOffer.margin;
    existingOffer.risk_adjustment = instantOffer.risk_adjustment;
    existingOffer.risk_level = instantOffer.risk_level;
    existingOffer.max_bid_price = instantOffer.max_bid_price;
    existingOffer.start_bid_price = instantOffer.start_bid_price;
    existingOffer.sales_price_estimate = instantOffer.sales_price_estimate;
    existingOffer.sales_price_estimate_source =
      instantOffer.sales_price_estimate_source;
    existingOffer.is_upgrade_or_purchase_interested =
      instantOffer.is_upgrade_or_purchase_interested;

    this.save(existingOffer);
  }

  remove(id: InstantOfferV2Type['id']) {
    const idToUse = id; // No String() casting needed for UUID
    const offer = this.getById(idToUse);
    if (!offer) return;
    this.store.delete(idToUse);
    this.removeFromIndexes(offer);

    db.instant_offers_v2.delete(idToUse);
  }
}

export const instantOfferStoreV2 = new InstantOfferStoreV2();
